import React, { FunctionComponent, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { HiPencil } from 'react-icons/hi2';
import Loader from 'react-loader-spinner';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import { IoIosAddCircle } from 'react-icons/io';
import { LuBookOpen } from 'react-icons/lu';
import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { meetingArchiveSelectors } from '../../../../app/resource/meeting-archives/meeting-archives.selectors';
import { meetingsArchivesPageSelectors } from '../../../../app/page/page-storage/meeting-archive/meeting-archive.selectors';
import { IMeetingArchive } from '../../../../app/resource/meeting-archives/meeting-archives.types';
import { fetchMeetingArchives } from '../../../../app/page/page-storage/meeting-archive/meeting-archive.actions';
import { IMeetingSimple } from '../../../../app/resource/meeting-simple/meeting-simple.types';
import { Pagination } from '../../common/Pagination';
import { convertAndFormatDateToUserTimeZone } from '../../../../utils/date.utils';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import { openDialog } from '../../../../app/dialog/dialog.actions';
import { ISession } from '../../../../app/resource/session/session.types';
import FirefyIcon from '../../../resources/images/fireflies.svg';
import ReadAiIcon from '../../../resources/images/read-ai.png';
import FathomIcon from '../../../resources/images/fathom-icon.png';
import ZoomIcon from '../../../resources/images/zoom-icon.svg';
import { InfoIconPopup } from '../../common/InfoIconPopup';
import { Tooltip } from '../../../common/Tooltip';
import { CirclePlusButton } from '../../common/CirclePlusButton';

const IN_PROGRESS_TRANSCRIPT_STATUSES = ['bot_joined', 'transcript_initiated'];
const TRANSCRIPT_STATUS_GRACE_PERIOD_MINUTES = 30;

const isStaleTranscriptStatus = (status?: string, finishedDate?: string | null): boolean => {
  if (!status || !IN_PROGRESS_TRANSCRIPT_STATUSES.includes(status) || !finishedDate) {
    return false;
  }

  return moment.utc().isAfter(moment.utc(finishedDate).add(TRANSCRIPT_STATUS_GRACE_PERIOD_MINUTES, 'minutes'));
};

const MeetingArchives: FunctionComponent = () => {
  const { meetingId } = useParams<{ meetingId: IMeetingSimple['id'] }>();
  const archivesRef = useRef<HTMLDivElement>(null);
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const sessionId = params.get('sessionId');
  const history = useHistory();

  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');

  const dispatch = useDispatch();

  const meetingArchivesIds = useSelect(
    meetingsArchivesPageSelectors.selectMeetingArchivesIds,
  ) as IMeetingArchive['id'][];
  const meetingArchives = useSelectWithParams(
    meetingArchiveSelectors.selectResourcesById,
    meetingArchivesIds,
  ) as IMeetingArchive[];

  const currentPage = useSelect(meetingsArchivesPageSelectors.selectMeetingArchiveCurrentPage);
  const totalPages = useSelect(meetingsArchivesPageSelectors.selectMeetingArchiveTotalPages);

  const openMeetingArchiveLinkFormDialog = useCallback(
    (sessionId: ISession['id']) => {
      dispatch(openDialog('meetingArchiveLinkFormDialog', { sessionId }));
    },
    [dispatch],
  );

  const openMeetingArchiveNoteFormDialog = useCallback(
    (sessionId: ISession['id']) => {
      dispatch(openDialog('meetingArchiveNoteFormDialog', { sessionId }));
    },
    [dispatch],
  );

  const goToPage = (page: number | null) => {
    dispatch(fetchMeetingArchives(meetingId, { page }));
  };

  const goToMeetingArchive = useCallback(
    (sessionId) => {
      if (sessionId) {
        history.push(`/meeting-archives/${sessionId}`);
      }
    },
    [history],
  );

  useEffect(() => {
    if (hash === '#archives' && archivesRef.current && sessionId) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            openMeetingArchiveLinkFormDialog(sessionId);
            observer.disconnect();
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(archivesRef.current);
      archivesRef.current.scrollIntoView({ behavior: 'smooth' });

      return () => observer.disconnect();
    }
  }, [hash, sessionId, openMeetingArchiveLinkFormDialog]);

  return (
    loggedUser && (
      <div ref={archivesRef} className="panel vto-text">
        <div className="panel-header small-header no-border pb-0">
          <h2 className="bold">Meeting Archives</h2>
          <div className="ml-auto flex aic"></div>
        </div>
        <div className="cards-container-overflow">
          <div className="cards-container">
            <div className="panel-body px-0 w100">
              <div className="border-radius-1 lightGray-bg p-2">
                <div className="row">
                  <div className="col">
                    <div className="table-container">
                      <div className="flex-table archive-table header panel mb-1 aic py-0 h20">
                        <div className="flex-row" role="columnheader" style={{ width: '15%' }}>
                          Meeting Date
                        </div>
                        <div className="flex-row" role="columnheader" style={{ width: '15%' }}>
                          Average Score
                        </div>
                        <div className="align-center flex flex-row h100" role="columnheader" style={{ width: '15%' }}>
                          <span>Meeting Recording</span>
                          <InfoIconPopup>
                            <div className="flex align-center" style={{ textTransform: 'none' }}>
                              <img className="mr-05" src={FirefyIcon} style={{ height: 15 }} /> Fireflies
                              <span className="mx-05"> • </span>
                              <img className="mr-05" src={ReadAiIcon} style={{ height: 22 }} /> Read.ai
                              <span className="mx-05"> • </span>
                              <img className="mr-05" src={ZoomIcon} style={{ height: 15 }} /> Zoom
                              <span className="mx-05"> • </span>
                              <img className="mr-05" src={FathomIcon} style={{ height: 15 }} /> Fathom and others.
                            </div>
                          </InfoIconPopup>
                        </div>
                        <div className="flex-row" role="columnheader" style={{ width: '15%' }}>
                          AI Agent
                        </div>
                        <div className="flex-row" role="columnheader" style={{ width: '25%' }}>
                          Note
                        </div>
                        <div className="flex-row" role="columnheader"></div>
                      </div>
                      {meetingArchives.length > 0 ? (
                        meetingArchives.map((meetingArchive) => (
                          <div className="flex-table align-center archive-table" key={meetingArchive.id}>
                            <div className="flex-row" style={{ width: '15%' }}>
                              {convertAndFormatDateToUserTimeZone(
                                meetingArchive.meetingDate,
                                loggedUser.timezone,
                                'MMMM Do, YYYY',
                              )}
                            </div>
                            <div className="flex-row" style={{ width: '15%' }}>
                              {meetingArchive.avgScore}
                            </div>
                            <MeetingLink
                              url={meetingArchive.link}
                              openMeetingArchiveFormDialog={() => openMeetingArchiveLinkFormDialog(meetingArchive.id)}
                            />
                            <MeetingTranscript
                              status={meetingArchive.transcriptStatus}
                              finishedDate={meetingArchive.finishedDate}
                              meetingId={meetingId}
                              sessionId={meetingArchive.id}
                            />
                            <MeetingNote
                              content={meetingArchive.note}
                              openMeetingArchiveFormDialog={() => openMeetingArchiveNoteFormDialog(meetingArchive.id)}
                            />
                            <div className="flex justify-end" style={{ marginLeft: 'auto' }}>
                              <span
                                className="btn btn-md purple-bg mr-1"
                                onClick={() => goToMeetingArchive(meetingArchive.id)}
                              >
                                See Meeting Archive
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-center mt-2">No Meeting Archives</div>
                      )}
                      {currentPage != null && totalPages != null && (
                        <Pagination pageSelected={currentPage} onChange={goToPage} totalPages={totalPages} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};
export default MeetingArchives;

const MeetingLink: FunctionComponent<{
  url?: string;
  openMeetingArchiveFormDialog: () => void;
}> = ({ url, openMeetingArchiveFormDialog }) => {
  let icon,
    appName: string | undefined = undefined;
  if (url) {
    if (url.includes('read.ai')) {
      appName = 'Read.ai';
      icon = ReadAiIcon;
    } else if (url.includes('fireflies.ai')) {
      appName = 'Fireflies';
      icon = FirefyIcon;
    } else if (url.includes('zoom.us')) {
      appName = 'Zoom';
      icon = ZoomIcon;
    } else if (url.includes('fathom.video')) {
      appName = 'Fathom';
      icon = FathomIcon;
    }
  }

  return (
    <div className="flex flex-row align-center theme-color pointer popup_button" role="cell" style={{ width: '15%' }}>
      {url ? (
        <>
          <HiPencil
            onClick={openMeetingArchiveFormDialog}
            color={'#FFFFFF'}
            size={24}
            className="mr-05 pointer theme-bg-color border-radius-50"
            style={{ marginLeft: 3, padding: 5, minWidth: 'fit-content' }}
          />
          {icon ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex align-center"
              style={{ color: '#66717c', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              <img src={icon} className="mr-05" style={{ height: 15 }} /> See in {appName}
            </a>
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#66717c', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {url}
            </a>
          )}
        </>
      ) : (
        <CirclePlusButton title="Add Meeting Archive" size={25} onClick={openMeetingArchiveFormDialog} />
        // onClick={openMeetingArchiveFormDialog}
        // color="var(--theme-primary-color)"
        // size={30}
        // className="mr-05 pointer"
        // />
      )}
    </div>
  );
};

const MeetingTranscript: FunctionComponent<{
  status?: string;
  finishedDate?: string | null;
  meetingId: IMeetingSimple['id'];
  sessionId: ISession['id'];
}> = ({ status, finishedDate, meetingId, sessionId }) => {
  const history = useHistory();

  const goToMeetingTranscript = useCallback(() => {
    if (meetingId && sessionId) {
      history.push(`/meetings/${meetingId}/transcripts/${sessionId}`);
    }
  }, [history, sessionId, meetingId]);

  let text, icon;
  if (status) {
    if (status === 'bot_joined' && !isStaleTranscriptStatus(status, finishedDate)) {
      text = (
        <div className="flex align-center">
          <span className="green italic">Recording</span>
          <Loader type="ThreeDots" color="#1bc3a7" height={3} width={15} />
        </div>
      );
    } else if (status === 'transcript_initiated' && !isStaleTranscriptStatus(status, finishedDate)) {
      text = (
        <div className="flex align-center">
          <span className="green italic">Generating</span>
          <Loader type="ThreeDots" color="#1bc3a7" height={3} width={15} />
        </div>
      );
    } else if (status === 'transcript_error') {
      text = <span className="red">Failed</span>;
    } else if (status === 'transcript_fetched') {
      icon = (
        <Tooltip content="Transcript" direction="top" bgColor="#13ac92" textColor="white">
          <LuBookOpen onClick={goToMeetingTranscript} color={'#66717c'} size={15} className="mr-05 pointer" />
        </Tooltip>
      );
    }
  }

  return (
    <div className="flex flex-row align-center" role="cell" style={{ width: '15%' }}>
      {text}
      {icon}
    </div>
  );
};

const MeetingNote: FunctionComponent<{ content?: string; type?: string; openMeetingArchiveFormDialog: () => void }> = ({
  content,
  openMeetingArchiveFormDialog,
}) => {
  return (
    <div className="flex flex-row align-center theme-color pointer popup_button" role="cell" style={{ width: '25%' }}>
      {content ? (
        <>
          <HiPencil
            onClick={openMeetingArchiveFormDialog}
            color={'#FFFFFF'}
            size={24}
            className="mr-05 pointer theme-bg-color border-radius-50"
            style={{ marginLeft: 3, padding: 5, minWidth: 'fit-content' }}
          />
          <span style={{ color: '#66717c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {content}
          </span>
        </>
      ) : (
        <CirclePlusButton title="Add Note" size={25} onClick={openMeetingArchiveFormDialog} />
      )}
    </div>
  );
};
